import { html, LitElement } from "lit";
import { customElement, property } from 'lit/decorators.js';
import { localized, msg, str } from '@lit/localize';
import { when } from "lit/directives/when.js";

import style from './cas-bar.scss';
import "./cas-lang";

/**
 * Header bar for login status and CAS links.
 * @slot service-name - Name of CAS service
 * @slot other - Extra text to be placed on center of CAS bar
 */

@customElement('cas-bar')
@localized()
export class CasBar extends LitElement {
    static override styles = style;

    /**
     * Name of currently logged in user or empty if nobody is logged in.
     * @default ''
     */
    @property({ attribute: 'logged-user' })
    loggedUser = '';

    /**
     * URL for login link, if empty, the link will not be shown.
     * @default ''
     */
    @property({ attribute: 'login-url' })
    loginUrl = '';

    /**
     * URL for logout link, if empty, the link will not be shown.
     * @default ''
     */
    @property({ attribute: 'logout-url' })
    logoutUrl = '';

    /**
     * URL for password change, if empty, the link will not be shown.
     * @default ''
     */
    @property({ attribute: 'passwd-url' })
    passwdUrl = '';

    /**
     * URL for language switch, if empty, the link will not be shown.
     * @default ''
     */
    @property({ attribute: 'lang-switch-url' })
    langSwitchUrl = '';

    /**
     * Login mode. Possible values:
     * - "admin" - current user is an administrator and we want to stress out that caution is required
     * - "impostor" - an administrator has logged in to another user's account
     * - "token" - user is logged in via token instead of CAS account.
     * - "user" - standard user is logged in
     * - "anon" - anonymous session, nobody is logged in
     * - "none" - no logging in, show only link for language switch
     * @default 'anon'
     */
    @property()
    mode: "admin" | "impostor" | "token" | "user" | "anon" | "none" = "anon";

    private _renderUsername() {
        if (this.mode == 'none') return html``;
        if (this.mode == 'anon') {
            return html`<div id="user">${msg(str`Nie jesteś zalogowany`)}</div>`;
        }
        if (this.mode == 'token') {
            return html`<div id="user">${msg(str`Zalogowany token`)}: <b>${this.loggedUser}</b></div>`;
        }
        return html`<div id="user">${msg(str`Zalogowany użytkownik`)}: <b>${this.loggedUser}</b></div>`;
    }

    private _renderPasswordChangeAction() {
        if (["admin", "user"].includes(this.mode) && this.passwdUrl) {
            return html`<a href="${this.passwdUrl}"><span>${msg(str`zmień hasło`)}</span></a>`;
        }
        return html``;
    }

    private _renderLoginAction() {
        if (this.mode == 'none') return html``;
        if (this.mode == 'impostor') {
            if (this.logoutUrl) {
                return html`<a href="${this.logoutUrl}"><span>${msg(str`zakończ tryb użytkownika`)}</span></a>`;
            } else return html``;
        }
        if (this.mode == 'anon') {
            if (this.loginUrl) {
                return html`<a href="${this.loginUrl}"><span>${msg(str`zaloguj się`)}</span></a>`;
            } else return html``;
        }
        if (this.logoutUrl) {
            return html`<a href="${this.logoutUrl}"><span>${msg(str`wyloguj się`)}</span></a>`;
        } else return html``;
    }

    override render() {
        return html`
        <div data-mode="${this.mode}">
            <div><slot name="service-name"></slot></div>
            <div><slot name="other"></slot></div>
            <div>
                ${this._renderUsername()}
                <div id="actions">
                    <slot name="extra-actions"></slot>
                    ${when(this.langSwitchUrl, () => html`<cas-lang change-url="${this.langSwitchUrl}"></cas-lang>`)}
                    ${this._renderPasswordChangeAction()}
                    ${this._renderLoginAction()}
                </div>
            </div>
        </div>
        `;
    }

}

declare global {
    interface HTMLElementTagNameMap {
        'cas-bar': CasBar,
    }
}
